/**
 * Helper du thème 4e « Proportionnalité » (attendus 171-174, O8-O11).
 * Importé SEULEMENT par gen_171 à gen_174 (vérifié le 2026-09-23 : ni 1128 ni
 * 1129 ne l'importent) ; aucun solveur test-side ne couvre ces gens.
 *
 * Deux briques :
 *   1. Arithmétique RATIONNELLE exacte (aucun flottant IEEE) : quatrième
 *      proportionnelle, coefficients, pourcentages, formules.
 *   2. Figures SVG émises en élément additionnel `image` (data-URI dans
 *      config.imageUrl, canal normatif §12), chacune avec un `alt` fourni par
 *      l'appelant, qui décrit la figure sans jamais donner la réponse :
 *        - svgRepere : repère quadrillé et une à trois séries de points reliés
 *          (171, reconnaître la proportionnalité sur un graphique). Le trait
 *          relie les points entre eux et n'est JAMAIS prolongé vers l'origine ;
 *          aucune droite n'est tracée par l'origine, aucun mot sur la figure.
 *        - svgTrianglesEmboites : les triangles emboîtés du cours (19Thales4
 *          p. 5, « Agrandissement et réduction ») : sommet commun A, B′ sur la
 *          droite (AB), C′ sur la droite (AC), (B′C′) parallèle à (BC), lettres
 *          en KaTeX_Math italique, dessin JAMAIS au coefficient de l'énoncé
 *          (174).
 *        - svgCercle : un cercle, son centre O et un diamètre ou un rayon coté
 *          (173, longueur d'un cercle).
 *
 * Revue 2026-09-23 (vague 3) : bloc <desc> et parseDesc retirés (le <desc>
 * n'était lu par personne, et dans une <img> il n'est pas exposé aux lecteurs
 * d'écran ; les commentaires qui annonçaient un solveur étaient faux) ;
 * svgDeuxTriangles retiré (174 dessine désormais les triangles emboîtés du
 * cours) ; imgElement prend un `alt`.
 */

// ═══════════════════════════════════════════════════════════
// 1. ARITHMÉTIQUE RATIONNELLE EXACTE
// ═══════════════════════════════════════════════════════════

export const pgcd = (a, b) => {
    a = Math.abs(a); b = Math.abs(b);
    while (b) { [a, b] = [b, a % b]; }
    return a || 1;
};

/** Réduit une fraction, signe porté par le numérateur. */
export const rat = (p, q) => {
    if (q < 0) { p = -p; q = -q; }
    const g = pgcd(p, q);
    return { p: p / g, q: q / g };
};

export const ratMul = (a, b) => rat(a.p * b.p, a.q * b.q);
export const ratDiv = (a, b) => rat(a.p * b.q, a.q * b.p);
export const ratAdd = (a, b) => rat(a.p * b.q + b.p * a.q, a.q * b.q);
export const ratSub = (a, b) => rat(a.p * b.q - b.p * a.q, a.q * b.q);
export const ratEq = (a, b) => a.p * b.q === b.p * a.q;

/** Décimal FR "12,5", entier "12", ou fraction "3/4" → rationnel réduit. */
export const ratFromStr = (s) => {
    const t = String(s).trim().replace(/\s/g, '').replace('%', '');
    if (t.includes('/')) {
        const [a, b] = t.split('/');
        return rat(parseInt(a, 10), parseInt(b, 10));
    }
    const neg = t.startsWith('-');
    const u = neg ? t.slice(1) : t;
    const [ip, dp = ''] = u.split(',');
    const num = parseInt((ip + dp) || '0', 10) * (neg ? -1 : 1);
    return rat(num, 10 ** dp.length);
};

/** Rationnel de valeur (number entier) ; usage : ratN(6) = 6/1. */
export const ratN = (n) => ({ p: n, q: 1 });

/**
 * Rationnel → écriture décimale FR EXACTE (sans zéros finaux) si le
 * dénominateur réduit ne contient que des 2 et des 5 ; sinon null.
 */
export const ratToDecFr = (r) => {
    const { p, q } = rat(r.p, r.q);
    if (q === 1) return String(p);
    let a2 = 0, a5 = 0, qq = q;
    while (qq % 2 === 0) { qq /= 2; a2++; }
    while (qq % 5 === 0) { qq /= 5; a5++; }
    if (qq !== 1) return null; // non décimal
    const scale = Math.max(a2, a5);
    const numScaled = Math.abs(p) * (10 ** scale) / q; // entier exact
    const neg = p < 0 ? '-' : '';
    const s = String(Math.round(numScaled)).padStart(scale + 1, '0');
    const ip = s.slice(0, s.length - scale);
    const dp = s.slice(s.length - scale).replace(/0+$/, '');
    return neg + (dp ? `${ip},${dp}` : ip);
};

/** Nombre de décimales de l'écriture décimale exacte (Infinity si non décimal). */
export const nbDecimales = (r) => {
    const s = ratToDecFr(r);
    if (s === null) return Infinity;
    return s.includes(',') ? s.split(',')[1].length : 0;
};

/** Valeur numérique (comparaisons/tri uniquement, jamais pour l'affichage). */
export const ratVal = (r) => r.p / r.q;

/** Ajoute les espaces milliers (français) sur un entier/décimal FR. */
export const formatFr = (num) => {
    const str = String(num);
    const [int, dec] = str.split(',');
    const neg = int.startsWith('-');
    const intAbs = neg ? int.slice(1) : int;
    const intFmt = intAbs.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return (neg ? '-' : '') + (dec ? `${intFmt},${dec}` : intFmt);
};

/**
 * Écriture d'un montant en euros : deux décimales dès qu'il n'est pas entier
 * (« 5,40 », « 2,16 », « 12 »). Le nombre ne doit pas avoir plus de deux
 * décimales (sinon null : un prix ne s'écrit pas au millième).
 */
export const euros = (r) => {
    const s = ratToDecFr(r);
    if (s === null) return null;
    if (!s.includes(',')) return formatFr(s);
    const [ip, dp] = s.split(',');
    if (dp.length > 2) return null;
    return `${formatFr(ip)},${dp.padEnd(2, '0')}`;
};

/** Décimal FR → écriture KaTeX (virgule protégée « 13{,}6 »), sans les $. */
export const decTex = (s) => String(s).replace(/\s/g, '\\,').replace(',', '{,}');

// ═══════════════════════════════════════════════════════════
// 2. TABLEAUX D'ÉNONCÉ (repris du 5e, lisibles sombre/clair)
// ═══════════════════════════════════════════════════════════

const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
export const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
export const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

// ═══════════════════════════════════════════════════════════
// 3. FIGURES SVG
// ═══════════════════════════════════════════════════════════

/** Élément additionnel `image` (§12) portant un SVG en data-URI, avec son alt. */
export const imgElement = (svg, maxWidth = 'small', alt = '') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
        ...(alt ? { alt } : {}),
    } }],
});

/** Une lettre de point ou de variable, en KaTeX_Math italique (règle prof 12/09). */
export const lettreSvg = (l) => `<tspan font-family="KaTeX_Math, 'Times New Roman', serif" font-style="italic">${l}</tspan>`;

const nbFr = (v) => String(Number.isInteger(v) ? v : Math.round(v * 100) / 100).replace('.', ',');

export const COULEURS_SERIES = ['#1E3A8A', '#DC2626', '#059669'];
export const FORMES_SERIES = ['rond', 'carre', 'triangle'];

const marqueur = (forme, x, y, c) => {
    const f = (n) => n.toFixed(1);
    if (forme === 'carre') return `<rect x="${f(x - 3.6)}" y="${f(y - 3.6)}" width="7.2" height="7.2" fill="${c}"/>`;
    if (forme === 'triangle') return `<polygon points="${f(x)},${f(y - 4.8)} ${f(x - 4.5)},${f(y + 3.4)} ${f(x + 4.5)},${f(y + 3.4)}" fill="${c}"/>`;
    return `<circle cx="${f(x)}" cy="${f(y)}" r="3.8" fill="${c}"/>`;
};

/**
 * Géométrie du repère, partagée entre le dessin et les contrôles du générateur
 * (écart d'un point à la corde, distance à l'origine) : mêmes échelles.
 */
export const geometrieRepere = ({ xmax, ymax, xStep, yStep }) => {
    const w = 360, h = 290, mL = 42, mR = 30, mT = 30, mB = 42;
    const xFin = xmax + 0.7 * xStep;
    const yFin = ymax + 0.6 * yStep;
    const X = (x) => mL + (x / xFin) * (w - mL - mR);
    const Y = (y) => (h - mB) - (y / yFin) * (h - mB - mT);
    return { w, h, mL, mR, mT, mB, xFin, yFin, X, Y, x0: X(0), y0: Y(0) };
};

/**
 * Repère quadrillé + séries de points reliés (171).
 * @param {Array<{label?, points:Array<[x,y]>, couleur?, forme?}>} series - points en coordonnées
 * @param {Object} o - { xmax, ymax, xStep, yStep, xLabel, yLabel }
 * Les points d'une série sont reliés dans l'ordre des abscisses, du premier au
 * dernier point seulement (jamais prolongés). Étiquette de série (A, B, C) à
 * droite du dernier point.
 */
export const svgRepere = (series, o) => {
    const G = geometrieRepere(o);
    const { w, h, mR, mT, X, Y, x0, y0 } = G;
    const f = (n) => n.toFixed(1);
    let grid = '';
    for (let gx = o.xStep; gx <= o.xmax + 1e-9; gx += o.xStep) {
        grid += `<line x1="${f(X(gx))}" y1="${f(Y(G.yFin))}" x2="${f(X(gx))}" y2="${f(y0)}" stroke="#e2e8f0" stroke-width="1"/>`;
        grid += `<text x="${f(X(gx))}" y="${f(y0 + 15)}" text-anchor="middle" font-size="11" fill="#475569">${nbFr(gx)}</text>`;
    }
    for (let gy = o.yStep; gy <= o.ymax + 1e-9; gy += o.yStep) {
        grid += `<line x1="${f(x0)}" y1="${f(Y(gy))}" x2="${f(X(G.xFin))}" y2="${f(Y(gy))}" stroke="#e2e8f0" stroke-width="1"/>`;
        grid += `<text x="${f(x0 - 6)}" y="${f(Y(gy) + 4)}" text-anchor="end" font-size="11" fill="#475569">${nbFr(gy)}</text>`;
    }

    const traces = series.map((s, i) => {
        const c = s.couleur || COULEURS_SERIES[i % COULEURS_SERIES.length];
        const forme = s.forme || FORMES_SERIES[i % FORMES_SERIES.length];
        const pts = [...s.points].sort((a, b) => a[0] - b[0]);
        const poly = `<polyline points="${pts.map((p) => `${f(X(p[0]))},${f(Y(p[1]))}`).join(' ')}" fill="none" stroke="${c}" stroke-width="2"/>`;
        return poly + pts.map((p) => marqueur(forme, X(p[0]), Y(p[1]), c)).join('');
    }).join('');

    const etiquettes = series.filter((s) => s.label).map((s) => {
        const i = series.indexOf(s);
        const c = s.couleur || COULEURS_SERIES[i % COULEURS_SERIES.length];
        const last = [...s.points].sort((a, b) => a[0] - b[0]).pop();
        return `<text x="${f(X(last[0]) + 8)}" y="${f(Y(last[1]) + 5)}" font-size="14" font-weight="bold" fill="${c}">${s.label}</text>`;
    }).join('');

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial, Helvetica, sans-serif">`
        + '<rect x="0" y="0" width="100%" height="100%" fill="#ffffff"/>'
        + grid
        + `<line x1="${f(x0)}" y1="${f(y0)}" x2="${f(w - mR + 14)}" y2="${f(y0)}" stroke="#1e293b" stroke-width="1.5"/>`
        + `<line x1="${f(x0)}" y1="${f(y0)}" x2="${f(x0)}" y2="${f(mT - 12)}" stroke="#1e293b" stroke-width="1.5"/>`
        + `<polygon points="${f(w - mR + 20)},${f(y0)} ${f(w - mR + 11)},${f(y0 - 4)} ${f(w - mR + 11)},${f(y0 + 4)}" fill="#1e293b"/>`
        + `<polygon points="${f(x0)},${f(mT - 18)} ${f(x0 - 4)},${f(mT - 9)} ${f(x0 + 4)},${f(mT - 9)}" fill="#1e293b"/>`
        + `<text x="${f(x0 - 6)}" y="${f(y0 + 15)}" text-anchor="end" font-size="11" fill="#475569">0</text>`
        + `<text x="${f(w - 6)}" y="${f(y0 + 32)}" text-anchor="end" font-size="12" fill="#1e293b">${o.xLabel}</text>`
        + `<text x="${f(x0 + 10)}" y="${f(mT - 10)}" text-anchor="start" font-size="12" fill="#1e293b">${o.yLabel}</text>`
        + traces
        + etiquettes
        + '</svg>';
};

/**
 * Les triangles emboîtés du cours (19Thales4 p. 5) : le grand triangle et le
 * petit ont le sommet A en commun, les deux autres sommets du petit sont sur
 * les côtés du grand, et les troisièmes côtés sont parallèles.
 *   - reduction = true  : ABC est le grand, AB′C′ le petit (B′ sur [AB], C′ sur [AC]) ;
 *   - reduction = false : ABC est le petit, AB′C′ le grand (B sur [AB′], C sur [AC′]).
 * `ratioDessin` = côté du petit ÷ côté du grand SUR LE DESSIN : choisi par
 * l'appelant loin du vrai coefficient (la figure n'est jamais à l'échelle).
 * `forme` 0, 1 ou 2 : trois gabarits (A en bas à gauche, en bas à droite comme
 * le cours, en haut). Aucune longueur écrite sur la figure : les longueurs
 * sont dans l'énoncé, et les étiquettes ne peuvent pas se chevaucher.
 */
const GABARITS_EMBOITES = [
    { A: [34, 196], P: [326, 196], Q: [250, 40] },
    { A: [326, 196], P: [34, 196], Q: [112, 40] },
    { A: [180, 34], P: [36, 200], Q: [318, 200] },
];

export const svgTrianglesEmboites = ({ reduction, ratioDessin, forme = 0 }) => {
    const g = GABARITS_EMBOITES[forme % GABARITS_EMBOITES.length];
    const r = ratioDessin;
    const pt = (base, t) => [g.A[0] + t * (base[0] - g.A[0]), g.A[1] + t * (base[1] - g.A[1])];
    const P = g.P, Q = g.Q;
    const Pp = pt(P, r), Qp = pt(Q, r);
    // Noms : le grand triangle porte B, C (réduction) ou B′, C′ (agrandissement).
    const nomGrand = reduction ? ['B', 'C'] : ['B′', 'C′'];
    const nomPetit = reduction ? ['B′', 'C′'] : ['B', 'C'];
    const f = (n) => n.toFixed(1);
    const centre = [(g.A[0] + P[0] + Q[0]) / 3, (g.A[1] + P[1] + Q[1]) / 3];
    // Étiquette d'un sommet du grand triangle : dans le prolongement du centre vers le sommet.
    const horsSommet = (S, d = 15) => {
        const dx = S[0] - centre[0], dy = S[1] - centre[1];
        const n = Math.hypot(dx, dy) || 1;
        return [S[0] + d * dx / n, S[1] + d * dy / n + 5];
    };
    // Étiquette d'un point posé sur un côté [A S] : perpendiculaire au côté, côté extérieur.
    const horsCote = (M, S, d = 15) => {
        const ux = S[0] - g.A[0], uy = S[1] - g.A[1];
        const n = Math.hypot(ux, uy) || 1;
        let nx = -uy / n, ny = ux / n;
        if ((M[0] + nx - centre[0]) * nx + (M[1] + ny - centre[1]) * ny < 0) { nx = -nx; ny = -ny; }
        return [M[0] + d * nx, M[1] + d * ny + 5];
    };
    const lettre = (pos, nom) => `<text x="${f(pos[0])}" y="${f(pos[1])}" text-anchor="middle" font-size="16" fill="#0f172a">${lettreSvg(nom)}</text>`;
    const poly = (pts, fill, stroke) => `<polygon points="${pts.map((p) => `${f(p[0])},${f(p[1])}`).join(' ')}" fill="${fill}" stroke="${stroke}" stroke-width="2" stroke-linejoin="round"/>`;
    const point = (p) => `<circle cx="${f(p[0])}" cy="${f(p[1])}" r="2.6" fill="#0f172a"/>`;
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 240" width="360" height="240" font-family="Arial, Helvetica, sans-serif">'
        + '<rect x="0" y="0" width="360" height="240" fill="#ffffff"/>'
        + poly([g.A, P, Q], '#1E3A8A1F', '#1E3A8A')
        + poly([g.A, Pp, Qp], '#DC262626', '#DC2626')
        + [g.A, P, Q, Pp, Qp].map(point).join('')
        + lettre(horsSommet(g.A), 'A')
        + lettre(horsSommet(P), nomGrand[0])
        + lettre(horsSommet(Q), nomGrand[1])
        + lettre(horsCote(Pp, P), nomPetit[0])
        + lettre(horsCote(Qp, Q), nomPetit[1])
        + '</svg>';
};

/**
 * Un cercle, son centre O, et un diamètre (ou un rayon) coté (173).
 * @param {string} cote - l'étiquette, par exemple « 24 cm »
 * @param {boolean} rayon - true : un rayon [OM] ; false : un diamètre
 */
export const svgCercle = (cote, rayon = false) => {
    const w = 240, h = 210, cx = 120, cy = 108, r = 78;
    const x1 = rayon ? cx : cx - r;
    const milieu = (x1 + cx + r) / 2;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial, Helvetica, sans-serif">`
        + `<rect x="0" y="0" width="${w}" height="${h}" fill="#ffffff"/>`
        + `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#1E3A8A14" stroke="#1E3A8A" stroke-width="2.5"/>`
        + `<line x1="${x1}" y1="${cy}" x2="${cx + r}" y2="${cy}" stroke="#DC2626" stroke-width="2"/>`
        + `<circle cx="${cx}" cy="${cy}" r="3" fill="#0f172a"/>`
        + `<text x="${cx}" y="${cy + 20}" text-anchor="middle" font-size="15" fill="#0f172a">${lettreSvg('O')}</text>`
        + `<text x="${milieu}" y="${cy - 9}" text-anchor="middle" font-size="14" font-weight="bold" fill="#DC2626">${cote}</text>`
        + '</svg>';
};
